/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import {
  FilterOption,
  finderGenderOptions,
  withAllOption,
} from '@sportninja/common/features/playerFinder/filterOptions'
import type { SpareCandidateLocationFilter } from '@sportninja/common/features/playerFinder/types'
import {
  GENDER_OPTIONS,
  GENDER_UNSPECIFIED,
} from '@sportninja/common/features/profile/player/constants'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React, { useCallback, useState } from 'react'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import { usePositionOptions } from 'src/features/team/player/hooks/useRosterPlayer'

// Ant's Select needs a defined value, so the "All" sentinel (value undefined
// from withAllOption) is represented as this string inside the control and
// mapped back to undefined for the parent.
const ALL_VALUE = 'all'

export interface PlayerFinderFilterBarProps {
  sportId?: string
  positionId?: string
  genderId?: string
  /** SN-8295 — where the players are (a listed player's card location, a
   *  roster spare's profile address); empty = all locations. */
  location: SpareCandidateLocationFilter
  onPositionChange: (value?: string) => void
  onGenderChange: (value?: string) => void
  /** A patch of the location — the parent merges it into its state. */
  onLocationChange: (patch: SpareCandidateLocationFilter) => void
}

/** `withAllOption`'s "All" entry has an undefined value; Ant needs a defined
 *  one, so it becomes the ALL_VALUE sentinel inside the control. */
export const toSelectOptions = (options: FilterOption[]) =>
  options.map((o) => ({ label: o.label, value: o.value ?? ALL_VALUE }))

/** Maps a select value back to what the PARENT expects: `undefined` means "no
 *  filter", so the query param is omitted entirely. Exported for tests —
 *  Ant's Select can't be driven reliably through jsdom, and letting the
 *  sentinel leak would send `player_type_id=all` to the API. */
export const fromSelectValue = (value: string): string | undefined =>
  !value || value === ALL_VALUE ? undefined : value

/** SN-8295 — the province part of the location filter, from the input's
 *  province id. The API filters by that id only: a country without provinces
 *  turns the field into free text, which has no id and must never be sent as
 *  `province_id`. Exported for tests, like the sentinel mapping above. */
export const provinceFilterPatch = (
  provinceId: string | number | undefined
): SpareCandidateLocationFilter => ({
  provinceId:
    provinceId !== undefined && provinceId !== ''
      ? String(provinceId)
      : undefined,
})

const PlayerFinderFilterBar: React.FC<PlayerFinderFilterBarProps> = ({
  sportId,
  positionId,
  genderId,
  location,
  onPositionChange,
  onGenderChange,
  onLocationChange,
}) => {
  'use memo'
  // What was typed into State/Province for a country without provinces.
  const [provinceText, setProvinceText] = useState('')
  // Once a province list loads, a province has to be picked from it. Stable:
  // the input reports its list from an effect keyed on this callback.
  const dropTypedProvince = useCallback((provinces: readonly unknown[]) => {
    if (provinces.length > 0) {
      setProvinceText('')
    }
  }, [])
  const positionOptions = toSelectOptions(
    withAllOption(usePositionOptions(sportId ?? null), 'All Positions')
  )
  // SN-8309 — the default sends no gender, and the server then applies the
  // competition's own gender rule: every gender it allows, not every gender.
  const genderOptions = toSelectOptions(
    withAllOption(
      finderGenderOptions(GENDER_OPTIONS, GENDER_UNSPECIFIED),
      'All Eligible Genders'
    )
  )

  const handleChange =
    (onChange: (value?: string) => void) => (value: string) =>
      onChange(fromSelectValue(value))

  return (
    <div css={barStyle}>
      <div css={fieldStyle}>
        <SelectInput
          label={capitalizeWords('Position')}
          name='playerFinderPosition'
          disabled={false}
          options={positionOptions}
          value={positionId ?? ALL_VALUE}
          onChange={handleChange(onPositionChange)}
          fieldErrors={{}}
          customError={{}}
        />
      </div>
      <div css={fieldStyle}>
        <SelectInput
          label={capitalizeWords('Gender')}
          name='playerFinderGender'
          disabled={false}
          options={genderOptions}
          value={genderId ?? ALL_VALUE}
          onChange={handleChange(onGenderChange)}
          fieldErrors={{}}
          customError={{}}
        />
      </div>
      {/* SN-8295 — where the players are: listed players by their card's
          location, the team's own spares by their profile address. */}
      <div css={locationStyle}>
        <CountryAndProvinceInput
          countryValue={location.countryId || undefined}
          // Free text typed for a country without provinces shows in the
          // field but doesn't filter (it has no province id).
          provinceValue={location.provinceId || provinceText || undefined}
          onCountryChange={(countryId) => {
            setProvinceText('')
            onLocationChange({ countryId, provinceId: undefined })
          }}
          onProvinceChange={(value, provinceId) => {
            setProvinceText(provinceId === undefined ? value ?? '' : '')
            onLocationChange(provinceFilterPatch(provinceId))
          }}
          onProvincesLoaded={dropTypedProvince}
          valueMode='id'
          provinceValueMode='id'
          allowClear
          responsive
          // The bar bottom-aligns its fields; the input's own bottom margin
          // would lift Country and State/Province above City.
          containerStyle={locationInputStyle}
          labels={{
            country: capitalizeWords('Country'),
            province: capitalizeWords('State/Province'),
          }}
          placeholders={{
            country: 'All Countries',
            province: 'All States/Provinces',
          }}
        />
      </div>
      <div css={fieldStyle}>
        <TextInput
          name='playerFinderCity'
          label={capitalizeWords('City')}
          value={location.city ?? ''}
          setValue={(city: string) => onLocationChange({ city })}
          placeholder={capitalizeWords('All Cities')}
          fieldErrors={{}}
          customError={{}}
        />
      </div>
    </div>
  )
}

export default PlayerFinderFilterBar

// The filters sit in their own panel so they read as a group, distinct from the
// candidate cards below.
const barStyle = css`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px;
  margin-bottom: 16px;
  /* Recessed panel, matching the candidate list's well below it, so the two
     regions read as a set against the lighter modal body. */
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 12px;
  background-color: ${colors.SECONDARY_400};
`

const locationInputStyle = css`
  margin-bottom: 0;
`

// Country + State/Province sit side by side, together as wide as two filters.
const locationStyle = css`
  flex: 2 1 360px;
  min-width: 0;
`

// SelectInput and TextInput pack their label directly against the control. The
// gap is added here, scoped to this bar, rather than in the shared components
// other forms use — 8px, matching CountryAndProvinceInput's label→control gap.
const fieldStyle = css`
  flex: 1 1 180px;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
